import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { formatCurrency, formatNumber } from "@/lib/format";
import { BadgePercent, TrendingUp, TrendingDown, Minus } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}-${m}-${y}`;
};

const Delta = ({ current, previous }) => {
  if (current == null || previous == null) return null;
  if (previous === 0 && current === 0) return null;
  if (previous === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-700">
        <TrendingUp className="h-3 w-3" /> nieuw
      </span>
    );
  }
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.5) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-slate-500">
        <Minus className="h-3 w-3" /> gelijk
      </span>
    );
  }
  const up = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-emerald-700" : "text-rose-600"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? "+" : ""}{pct.toFixed(0)}%
    </span>
  );
};

const Metric = ({ label, value, delta, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-amber-700/70">{label}</p>
    <div className="flex items-baseline gap-2 mt-0.5">
      <p className="text-2xl font-semibold tracking-tight text-slate-900">{value}</p>
      {delta}
    </div>
  </div>
);

export default function ActieImpactCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/actie-impact").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data?.active) return null;

  const prev = data.vorige_periode || {};

  return (
    <Card className="bg-amber-50/70 border-amber-200 rounded-lg p-5 sm:p-6" data-testid="actie-impact-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <BadgePercent className="h-4 w-4 text-amber-600" />
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-amber-700">Actie-impact</p>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mt-0.5">
            {data.naam || "Actie"}
            <span className="text-slate-400 font-normal text-sm ml-2">
              {fmtDate(data.start_datum)} t/m {fmtDate(data.eind_datum)}
            </span>
          </h2>
        </div>
        <span
          className={`inline-flex items-center text-[11px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full border ${
            data.status === "lopend"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-slate-100 text-slate-600 border-slate-200"
          }`}
          data-testid="actie-status-badge"
        >
          {data.status === "lopend" ? `Dag ${data.dagen_verstreken} van ${data.dagen_totaal}` : "Afgelopen"}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Metric
          label="Palletten verkocht"
          value={formatNumber(data.aantal_palletten)}
          delta={<Delta current={data.aantal_palletten} previous={prev.aantal_palletten} />}
          testId="actie-metric-palletten"
        />
        <Metric
          label="Orders"
          value={formatNumber(data.aantal_orders)}
          delta={<Delta current={data.aantal_orders} previous={prev.aantal_orders} />}
          testId="actie-metric-orders"
        />
        <Metric
          label="Omzet (incl. btw)"
          value={formatCurrency(data.omzet_incl_btw)}
          delta={<Delta current={data.omzet_incl_btw} previous={prev.omzet_incl_btw} />}
          testId="actie-metric-omzet"
        />
        <Metric
          label="Brutomarge"
          value={formatCurrency(data.brutomarge)}
          delta={<Delta current={data.brutomarge} previous={prev.brutomarge} />}
          testId="actie-metric-marge"
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-amber-200/70 pt-3">
        {(data.producten || []).map((p) => (
          <span key={p.productnummer} className="text-xs text-slate-600" data-testid={`actie-product-${p.productnummer}`}>
            <span className="font-semibold text-slate-800">{formatNumber(p.aantal_verkocht)}×</span>{" "}
            {p.productnaam.replace("Lifjalla Water in a box ", "")}
          </span>
        ))}
        <span className="text-[11px] text-amber-700/70 ml-auto">
          Vergelijking: {fmtDate(prev.start_datum)} t/m {fmtDate(prev.eind_datum)} ({data.dagen_totaal} dagen ervoor)
        </span>
      </div>
    </Card>
  );
}
